import React from 'react';
import { Box, Collapse, Paper, Typography } from '@mui/material';

export type GemmaActivityLine = { id: string; verb: string; detail?: string; isLive?: boolean };

type Props = {
  lines: GemmaActivityLine[];
  expanded: boolean;
  onToggle: () => void;
};

/**
 * Podgląd kroków agenta (styl zbliżony do Cursora: „Thinking”, „Read …”).
 */
export default function GemmaAgentActivityPanel({ lines, expanded, onToggle }: Props) {
  if (lines.length === 0) return null;
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        border: '1px solid #e4e4ec',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#F3F3FA',
        '@keyframes gemmaActivityEnter': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes gemmaActivityPulse': {
          '0%, 100%': {
            borderColor: '#e4e4ec',
            boxShadow: '0 1px 4px rgba(63, 63, 220, 0.05)',
          },
          '50%': {
            borderColor: 'rgba(63, 63, 220, 0.38)',
            boxShadow: '0 2px 14px rgba(63, 63, 220, 0.14)',
          },
        },
        animation:
          'gemmaActivityEnter 0.38s ease-out forwards, gemmaActivityPulse 2.4s ease-in-out 0.35s infinite',
      }}
    >
      <Box
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'rgba(63,63,220,0.06)' },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2D2D32', fontSize: 13 }}>
          Gemma · aktywność
        </Typography>
        <Typography variant="caption" sx={{ color: '#6B6B76', fontSize: 14, lineHeight: 1 }}>
          {expanded ? '⌃' : '⌵'}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box
          sx={{
            px: 1.5,
            pb: 1.25,
            maxHeight: 220,
            overflowY: 'auto',
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {lines.map((ln) => (
            <Box
              key={ln.id}
              sx={{
                py: 0.4,
                pl: 1,
                mb: 0.35,
                borderLeft: ln.isLive ? '2px solid #3F3FDC' : '2px solid #d8d8e3',
                color: ln.isLive ? '#1a1a24' : '#7a7a88',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: ln.isLive ? 600 : 500,
                  color: ln.isLive ? '#111' : '#5c5c68',
                  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                }}
              >
                {ln.verb}
              </Box>
              {ln.detail ? (
                <Typography
                  component="div"
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.35,
                    color: '#5c5c68',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: 11,
                  }}
                >
                  {ln.detail}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
